import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { DeleteMarkupDialog } from "@pages/project-details/project-markups/markup-delete-dialog";
import { nounPluralize } from "@utils/data-display";
import { BulkActionEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, DeleteIcon, useToast } from "@flat-ui";
/** Show delete markup button and dispatch action where the delete dialog will act accordingly */
export function BulkDeleteMarkups({
  selectedMarkups,
  projectId
}) {
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    bulkDeleteMarkupsFromProject
  } = useProjectMarkupUpdate({
    projectId
  });
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const mode = useBulkActionsMode();

  /** Delete markups in bulk */
  async function deleteMarkupsInBulk() {
    trackEvent({
      name: BulkActionEvents.bulkDeleteMarkup,
      props: {
        dataType: TableType.projectMarkups,
        numberOfEntities: selectedMarkups.length
      }
    });
    try {
      setIsDeleting(true);
      const response = await bulkDeleteMarkupsFromProject(selectedMarkups);
      if (response && response.failedMutations.length === 0) {
        openToast({
          title: "Annotations deleted."
        });
      } else {
        openToast({
          title: `Failed to delete ${nounPluralize({
            counter: response.failedMutations.length,
            word: "annotation"
          })}`,
          variant: "error"
        });
      }
    } catch (error) {
      handleErrorWithToast({
        id: `Delete annotation-${Date.now().toString()}`,
        title: "Failed to delete the annotations.",
        error
      });
    } finally {
      setIsDeleting(false);
      setShouldShowDialog(false);
    }
  }
  const config = getBulkActionButtonConfig(mode, "Delete annotations");
  return <>
      <FaroTextButton onClick={() => setShouldShowDialog(true)} tooltipText={config.tooltipText} startIcon={<DeleteIcon sx={{
      width: "18px",
      height: "18px"
    }} />} rippleColor={config.rippleColor} sx={config.sx}>
        {config.shouldShowText && <Text variant="buttonM">Delete</Text>}
      </FaroTextButton>

      {shouldShowDialog && <DeleteMarkupDialog dialogContent={{
      title: "Delete annotations?",
      description: "This will remove the selected annotations from your project."
    }} onClose={() => setShouldShowDialog(false)} onConfirm={deleteMarkupsInBulk} isDeleting={isDeleting} />}
    </>;
}